import { zodResolver } from "@hookform/resolvers/zod"
import { Box, Dialog, DialogActions, DialogContent } from "@mui/material"
import type { FC } from "react"
import { useEffect, useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"

import { InventoryUntestedLotsDialog } from "../../../../components"
import { useAdjustmentLotSelection, useCreateCombinePalletsAdjustment } from "../../hooks"
import { type CombinePalletsAdjustmentFormValues, combinePalletsAdjustmentFormSchema } from "../../schemas"
import { toCombinePalletsAdjustmentCreateInput } from "../../utils"
import { AdjustmentLotSelection } from "../AdjustmentLotSelection"
import { AdjustmentTypeHeader } from "../AdjustmentTypeHeader"

import { CombinePalletsAdjustmentFormDetails } from "./CombinePalletsAdjustmentFormDetails"
import { CombinePalletsAdjustmentFormLots } from "./CombinePalletsAdjustmentFormLots"

import { Button } from "@/components/common/Button"
import CancelDialog from "@/components/common/Modals/CancelDialog"
import DialogHeader from "@/components/common/Modals/DialogHeader"
import { InventoryAdjustmentType, type InventoryByProductWarehouse, type Product } from "@/graphql/codegen/graphql"
import type { Option } from "@/types/form-fields"

interface CombinePalletsAdjustmentFormDialogProps {
  product: Product
  warehouseAreaId: string
  warehouseInventories: InventoryByProductWarehouse[]
  warehouseOptions: Option<string>[]
  onClose: () => void
  onSuccess: () => void
}

export const CombinePalletsAdjustmentFormDialog: FC<CombinePalletsAdjustmentFormDialogProps> = ({
  product,
  warehouseAreaId,
  warehouseInventories,
  warehouseOptions,
  onClose,
  onSuccess,
}) => {
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] = useState(false)

  const form = useForm<CombinePalletsAdjustmentFormValues>({
    resolver: zodResolver(combinePalletsAdjustmentFormSchema),
    defaultValues: {
      warehouseAreaId,
      targetReferenceNumber: null,
      comment: "",
      document: undefined,
    },
    mode: "onChange",
  })
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isValid, isSubmitting, errors },
  } = form
  const selectedWarehouseAreaId = watch("warehouseAreaId")
  const targetReferenceNumber = watch("targetReferenceNumber")

  const { createAdjustment } = useCreateCombinePalletsAdjustment()

  const selectedWarehouseArea = useMemo(
    () =>
      warehouseInventories.find((inventory) => inventory.warehouseArea?.warehouseAreaId === selectedWarehouseAreaId)
        ?.warehouseArea,
    [warehouseInventories, selectedWarehouseAreaId]
  )

  const {
    palletGroups,
    isFetching,
    selectedLotIds,
    selectedLots,
    untestedLots,
    sortBy,
    sortDirection,
    validateLotsTested,
    handleSearchChange,
    handleSortChange,
    handleSelectionChange,
    handleSelectionRemove,
    handleSelectionReset,
    handleUntestedLotsReset,
  } = useAdjustmentLotSelection({
    productExternalSystemId: product.externalSystemId!,
    warehouseAreaExternalSystemId: selectedWarehouseArea!.externalSystemId!,
  })

  const uomName = product.packageUsWeightUomDetails?.name ?? ""

  const selectedReferenceNumbers = useMemo(
    () => new Set(selectedLots.map((lot) => lot.pallet.referenceNbr)),
    [selectedLots]
  )

  useEffect(() => {
    if (targetReferenceNumber !== null && !selectedReferenceNumbers.has(targetReferenceNumber)) {
      setValue("targetReferenceNumber", null, { shouldValidate: true })
    }
  }, [selectedReferenceNumbers, targetReferenceNumber, setValue])

  const canSubmit = isValid && !isSubmitting && !errors.document && selectedReferenceNumbers.size >= 2

  const handleWarehouseChange = () => {
    handleSelectionReset()
  }

  const handleCancel = () => {
    setIsCancelConfirmationOpen(true)
  }

  const submitForm = async (formValues: CombinePalletsAdjustmentFormValues) => {
    if (!validateLotsTested()) return

    const isSuccess = await createAdjustment(
      toCombinePalletsAdjustmentCreateInput(formValues, Number(product.productId), selectedLots),
      formValues.comment,
      formValues.document
    )

    if (isSuccess) onSuccess()
  }

  if (isCancelConfirmationOpen) {
    return <CancelDialog onClose={() => setIsCancelConfirmationOpen(false)} onConfirm={onClose} />
  }

  if (untestedLots.length > 0) {
    return <InventoryUntestedLotsDialog untestedLots={untestedLots} onClose={handleUntestedLotsReset} />
  }

  return (
    <Dialog
      open
      fullWidth
      maxWidth={false}
      PaperProps={{ className: "h-full" }}
      onClose={handleCancel}
      data-testid='inventory.combine-pallets-adjustment-form-dialog'
    >
      <DialogHeader title='Inventory Adjustment & Pallet Operations' onClose={handleCancel} />
      <FormProvider {...form}>
        <AdjustmentTypeHeader
          adjustmentType={InventoryAdjustmentType.CombinePallets}
          product={product}
          warehouseOptions={warehouseOptions}
          onWarehouseChange={handleWarehouseChange}
        />
        <DialogContent className='grid flex-1 grid-cols-[1fr_368px] gap-x-6 overflow-hidden px-10 pb-10 pt-6'>
          <Box className='flex min-w-0 flex-col gap-y-6 overflow-hidden'>
            <AdjustmentLotSelection
              palletGroups={palletGroups}
              isFetching={isFetching}
              selectedLotIds={selectedLotIds}
              sortBy={sortBy}
              sortDirection={sortDirection}
              uomName={uomName}
              onSelectionChange={handleSelectionChange}
              onSortChange={handleSortChange}
              onSearchChange={handleSearchChange}
            />
            <CombinePalletsAdjustmentFormLots
              selectedLots={selectedLots}
              uomName={uomName}
              onLotRemove={handleSelectionRemove}
            />
          </Box>
          <CombinePalletsAdjustmentFormDetails referenceNumbers={selectedReferenceNumbers} />
        </DialogContent>
      </FormProvider>
      <DialogActions className='justify-between px-10 pb-10'>
        <Button
          appearance='outlined'
          onClick={handleCancel}
          data-testid='inventory.combine-pallets-adjustment-form-dialog.cancel-button'
        >
          Cancel
        </Button>
        <Button
          appearance='contained'
          disabled={!canSubmit}
          onClick={handleSubmit(submitForm)}
          data-testid='inventory.combine-pallets-adjustment-form-dialog.submit-button'
        >
          Confirm Adjustment
        </Button>
      </DialogActions>
    </Dialog>
  )
}
